import { normalizeFromEndpoint } from './endpointUtils';

/**
 * Gets platform-specific configuration for voice platforms
 * @param {Object} activeChannelConfig - The active channel configuration
 * @returns {Object} Platform-specific configuration
 */
const getPlatformSpecificConfig = (activeChannelConfig) => {
  const platform = activeChannelConfig.platform || 'bland_ai';
  
  switch (platform) {
    case 'bland_ai':
      return {
        platform_config: {
          task: activeChannelConfig.platform_config?.task || '',
          pathway_id: activeChannelConfig.platform_config?.pathway_id || '',
          ...(activeChannelConfig.platform_config?.pathway_version !== undefined && { pathway_version: activeChannelConfig.platform_config.pathway_version }),
          first_sentence: activeChannelConfig.platform_config?.first_sentence || '',
          background_track: activeChannelConfig.platform_config?.background_track || '',
          model: activeChannelConfig.platform_config?.model || 'base',
          wait_for_greeting: activeChannelConfig.platform_config?.wait_for_greeting ?? false,
          block_interruptions: activeChannelConfig.platform_config?.block_interruptions ?? false,
          interruption_threshold: activeChannelConfig.platform_config?.interruption_threshold || 100,
          sensitive_voicemail_detection: activeChannelConfig.platform_config?.sensitive_voicemail_detection ?? false,
          transfer_phone_number: activeChannelConfig.platform_config?.transfer_phone_number || '',
          transfer_list: activeChannelConfig.platform_config?.transfer_list || {},
          language: activeChannelConfig.platform_config?.language || 'en-US',
          voicemail_sms: activeChannelConfig.platform_config?.voicemail_sms || {},
          dispatch_hours: activeChannelConfig.platform_config?.dispatch_hours || {},
          ignore_button_press: activeChannelConfig.platform_config?.ignore_button_press ?? false,
          timezone: activeChannelConfig.platform_config?.timezone || 'America/New_York',
          request_data: activeChannelConfig.platform_config?.request_data || {},
          tools: activeChannelConfig.platform_config?.tools || [],
          start_time: activeChannelConfig.platform_config?.start_time || '',
          voicemail_action: activeChannelConfig.platform_config?.voicemail_action || {},
          retry: activeChannelConfig.platform_config?.retry || { wait: 300, voicemail_action: 'hangup', voicemail_message: '' },
          max_duration: activeChannelConfig.platform_config?.max_duration || 5,
          record: activeChannelConfig.platform_config?.record ?? false,
          webhook: activeChannelConfig.platform_config?.webhook || '',
          webhook_events: activeChannelConfig.platform_config?.webhook_events || [],
          metadata: activeChannelConfig.platform_config?.metadata || {},
          citation_schema_id: activeChannelConfig.platform_config?.citation_schema_id || '',
          ...(activeChannelConfig.platform_config?.analysis_preset !== undefined && { analysis_preset: activeChannelConfig.platform_config.analysis_preset }),
          available_tags: activeChannelConfig.platform_config?.available_tags || [],
          ...(activeChannelConfig.platform_config?.geospatial_dialing !== undefined && { geospatial_dialing: activeChannelConfig.platform_config.geospatial_dialing }),
          precall_dtmf_sequence: activeChannelConfig.platform_config?.precall_dtmf_sequence || '',
          dynamic_data: activeChannelConfig.platform_config?.dynamic_data || [],
          keywords: activeChannelConfig.platform_config?.keywords || [],
          pronunciation_guide: activeChannelConfig.platform_config?.pronunciation_guide || [],
          local_dialing: activeChannelConfig.platform_config?.local_dialing ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'vapi':
      return {
        platform_config: {
          voice_id: activeChannelConfig.voice_id || 'shimmer',
          voice_name: activeChannelConfig.voice_name || 'Shimmer (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 10,
          voicemail_detection: {
            enabled: activeChannelConfig.platform_config?.voicemail_detection?.enabled ?? true,
            sensitivity: activeChannelConfig.platform_config?.voicemail_detection?.sensitivity || 'high'
          },
          call_timeout: activeChannelConfig.call_timeout || 30,
          record_call: activeChannelConfig.record_call ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'elevenlabs':
      return {
        platform_config: {
          voice_id: activeChannelConfig.voice_id || '21m00Tcm4TlvDq8ikWAM',
          voice_name: activeChannelConfig.voice_name || 'Rachel (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 5,
          voicemail_handling: {
            detect: activeChannelConfig.platform_config?.voicemail_handling?.detect ?? true,
            action: activeChannelConfig.platform_config?.voicemail_handling?.action || 'leave_message'
          },
          call_timeout: activeChannelConfig.call_timeout || 30,
          record_call: activeChannelConfig.record_call ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'twilio':
      return {
        platform_config: {
          voice_id: activeChannelConfig.voice_id || 'alexa',
          voice_name: activeChannelConfig.voice_name || 'Alexa (Female)',
          machine_detection: activeChannelConfig.platform_config?.machine_detection || 'DetectMessageEnd',
          machine_detection_timeout: activeChannelConfig.platform_config?.machine_detection_timeout || 30,
          call_timeout: activeChannelConfig.call_timeout || 60,
          record_call: activeChannelConfig.record_call ?? true,
          ...activeChannelConfig.platform_config
        }
      };
    default:
      return {};
  }
};

const getPlatformSpecificConfigWithId = (activeChannelConfig) => {
  const platform = activeChannelConfig.platform || 'bland_ai';
  
  switch (platform) {
    case 'bland_ai':
      return {
        platform_config: {
          // Preserve the platform config ID
          id: activeChannelConfig.platform_config?.id,
          task: activeChannelConfig.platform_config?.task || '',
          pathway_id: activeChannelConfig.platform_config?.pathway_id || '',
          ...(activeChannelConfig.platform_config?.pathway_version !== undefined && { pathway_version: activeChannelConfig.platform_config.pathway_version }),
          first_sentence: activeChannelConfig.platform_config?.first_sentence || '',
          background_track: activeChannelConfig.platform_config?.background_track || '',
          model: activeChannelConfig.platform_config?.model || 'base',
          wait_for_greeting: activeChannelConfig.platform_config?.wait_for_greeting ?? false,
          block_interruptions: activeChannelConfig.platform_config?.block_interruptions ?? false,
          interruption_threshold: activeChannelConfig.platform_config?.interruption_threshold || 100,
          sensitive_voicemail_detection: activeChannelConfig.platform_config?.sensitive_voicemail_detection ?? false,
          transfer_phone_number: activeChannelConfig.platform_config?.transfer_phone_number || '',
          transfer_list: activeChannelConfig.platform_config?.transfer_list || {},
          language: activeChannelConfig.platform_config?.language || 'en-US',
          voicemail_sms: activeChannelConfig.platform_config?.voicemail_sms || {},
          dispatch_hours: activeChannelConfig.platform_config?.dispatch_hours || {},
          ignore_button_press: activeChannelConfig.platform_config?.ignore_button_press ?? false,
          timezone: activeChannelConfig.platform_config?.timezone || 'America/New_York',
          request_data: activeChannelConfig.platform_config?.request_data || {},
          tools: activeChannelConfig.platform_config?.tools || [],
          start_time: activeChannelConfig.platform_config?.start_time || '',
          voicemail_action: activeChannelConfig.platform_config?.voicemail_action || {},
          retry: activeChannelConfig.platform_config?.retry || { wait: 300, voicemail_action: 'hangup', voicemail_message: '' },
          max_duration: activeChannelConfig.platform_config?.max_duration || 5,
          record: activeChannelConfig.platform_config?.record ?? false,
          webhook: activeChannelConfig.platform_config?.webhook || '',
          webhook_events: activeChannelConfig.platform_config?.webhook_events || [],
          metadata: activeChannelConfig.platform_config?.metadata || {},
          citation_schema_id: activeChannelConfig.platform_config?.citation_schema_id || '',
          ...(activeChannelConfig.platform_config?.analysis_preset !== undefined && { analysis_preset: activeChannelConfig.platform_config.analysis_preset }),
          available_tags: activeChannelConfig.platform_config?.available_tags || [],
          ...(activeChannelConfig.platform_config?.geospatial_dialing !== undefined && { geospatial_dialing: activeChannelConfig.platform_config.geospatial_dialing }),
          precall_dtmf_sequence: activeChannelConfig.platform_config?.precall_dtmf_sequence || '',
          dynamic_data: activeChannelConfig.platform_config?.dynamic_data || [],
          keywords: activeChannelConfig.platform_config?.keywords || [],
          pronunciation_guide: activeChannelConfig.platform_config?.pronunciation_guide || [],
          local_dialing: activeChannelConfig.platform_config?.local_dialing ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'vapi':
      return {
        platform_config: {
          // Preserve the platform config ID
          id: activeChannelConfig.platform_config?.id,
          voice_id: activeChannelConfig.voice_id || 'shimmer',
          voice_name: activeChannelConfig.voice_name || 'Shimmer (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 10,
          voicemail_detection: {
            enabled: activeChannelConfig.platform_config?.voicemail_detection?.enabled ?? true,
            sensitivity: activeChannelConfig.platform_config?.voicemail_detection?.sensitivity || 'high'
          },
          call_timeout: activeChannelConfig.call_timeout || 30,
          record_call: activeChannelConfig.record_call ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'elevenlabs':
      return {
        platform_config: {
          // Preserve the platform config ID
          id: activeChannelConfig.platform_config?.id,
          voice_id: activeChannelConfig.voice_id || '21m00Tcm4TlvDq8ikWAM',
          voice_name: activeChannelConfig.voice_name || 'Rachel (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 5,
          voicemail_handling: {
            detect: activeChannelConfig.platform_config?.voicemail_handling?.detect ?? true,
            action: activeChannelConfig.platform_config?.voicemail_handling?.action || 'leave_message'
          },
          call_timeout: activeChannelConfig.call_timeout || 30,
          record_call: activeChannelConfig.record_call ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    case 'twilio':
      return {
        platform_config: {
          // Preserve the platform config ID
          id: activeChannelConfig.platform_config?.id,
          voice_id: activeChannelConfig.voice_id || 'alexa',
          voice_name: activeChannelConfig.voice_name || 'Alexa (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 5,
          call_timeout: activeChannelConfig.call_timeout || 30,
          record_call: activeChannelConfig.record_call ?? false,
          ...activeChannelConfig.platform_config
        }
      };
    default:
      return {
        platform_config: {
          // Preserve the platform config ID
          id: activeChannelConfig.platform_config?.id,
          ...activeChannelConfig.platform_config
        }
      };
  }
};

/**
 * Serializes a drip campaign for API submission
 * @param {Object} campaign - The campaign data to serialize
 * @returns {Object} The serialized campaign data
 */
export const serializeDripCampaign = (campaign) => {
  // Clean up message steps with voice platform support
  const cleanedMessageSteps = campaign.drip_schedule.message_steps.map(step => {
    const activeChannelConfig = step[`${campaign.channel}_config`] || {};
    
    if (campaign.channel === 'voice') {
      // Use step-level voice_config if available, otherwise fall back to channel config
      const voiceConfig = step.voice_config || activeChannelConfig;
      return {
        ...step,
        voice_config: {
          content: voiceConfig.content || '',
          from_endpoint: voiceConfig.from_endpoint || '',
          from_endpoint_value: voiceConfig.from_endpoint_value || '',
          platform: voiceConfig.platform || 'bland_ai',
          voice_id: voiceConfig.voice_id || 'alexa',
          voice_name: voiceConfig.voice_name || 'Alexa (Female)',
          temperature: voiceConfig.temperature || 0.7,
          max_duration: voiceConfig.max_duration || 5,
          voicemail_message: voiceConfig.voicemail_message || '',
          voicemail_action: voiceConfig.voicemail_action || 'hangup',
          record_call: voiceConfig.record_call ?? true,
          call_timeout: voiceConfig.call_timeout || 60,
          // Platform-specific fields
          ...getPlatformSpecificConfig(voiceConfig),
          webhook_config: {
            webhook: voiceConfig.webhook_config?.webhook || '',
            webhook_events: voiceConfig.webhook_config?.webhook_events || [],
            ...voiceConfig.webhook_config
          },
          metadata: voiceConfig.metadata || {}
        }
      };
    } else if (campaign.channel === 'email') {
      return {
        ...step,
        email_config: {
          content: activeChannelConfig.content || '',
          subject: activeChannelConfig.subject || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          from_name: activeChannelConfig.from_name || '',
          reply_to: activeChannelConfig.reply_to || '',
          priority: activeChannelConfig.priority || 'normal',
          track_opens: activeChannelConfig.track_opens ?? true,
          track_clicks: activeChannelConfig.track_clicks ?? true,
          attachments: activeChannelConfig.attachments || []
        }
      };
    } else if (campaign.channel === 'sms') {
      return {
        ...step,
        sms_config: {
          content: activeChannelConfig.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          priority: activeChannelConfig.priority || 'normal',
          track_delivery: activeChannelConfig.track_delivery ?? true,
          track_replies: activeChannelConfig.track_replies ?? true,
          media_urls: activeChannelConfig.media_urls || []
        }
      };
    } else if (campaign.channel === 'chat') {
      return {
        ...step,
        chat_config: {
          content: activeChannelConfig.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          platform: activeChannelConfig.platform || 'whatsapp',
          priority: activeChannelConfig.priority || 'normal',
          track_delivery: activeChannelConfig.track_delivery ?? true,
          track_read: activeChannelConfig.track_read ?? true,
          media_urls: activeChannelConfig.media_urls || [],
          quick_replies: activeChannelConfig.quick_replies || []
        }
      };
    }
    
    return step;
  });
  
  return {
    // Campaign metadata
    name: campaign.name || '',
    description: campaign.description || '',
    account: campaign.account || null,
    crm_campaign: campaign.crm_campaign || null,
    start_date: campaign.start_date || null,
    end_date: campaign.end_date || null,
    is_ongoing: campaign.is_ongoing ?? false,
    active: campaign.active ?? true,
    campaign_type: 'drip',
    channel: campaign.channel || 'email',
    template: campaign.template || null,
    status: campaign.status || 'draft',
    auto_enroll_new_leads: campaign.auto_enroll_new_leads ?? false,
    auto_enroll_filters: campaign.auto_enroll_filters || [],
    lead_filters: campaign.lead_filters || [],
    enable_opt_out: campaign.enable_opt_out ?? false,
    initial_opt_out_notice: campaign.initial_opt_out_notice || '',
    opt_out_message: campaign.opt_out_message || '',
    // Retry configuration
    retry_strategy_id: campaign.retry_strategy_id || null,
    max_retries: campaign.max_retries || null,
    // Channel configs (voice config is only stored at step level for drip campaigns)
    email_config: campaign.channel === 'email' ? campaign.email_config : null,
    sms_config: campaign.channel === 'sms' ? campaign.sms_config : null,
    voice_config: null, // Voice config is only stored at message step level for drip campaigns
    chat_config: campaign.channel === 'chat' ? campaign.chat_config : null,
    // Drip schedule with cleaned message steps
    drip_schedule: {
      ...campaign.drip_schedule,
      message_steps: cleanedMessageSteps
    }
  };
};

/**
 * Serializes a reminder campaign for API submission
 * @param {Object} campaign - The campaign data to serialize
 * @returns {Object} The serialized campaign data
 */
export const serializeReminderCampaign = (campaign) => {
  // Clean up reminder times
  const cleanedReminderTimes = campaign.reminder_schedule.reminder_times.map(time => {
    const cleanedTime = {
      // Preserve the reminder time ID
      id: time.id,
      days_before: campaign.reminder_schedule.use_relative_schedule ? null : (time.days_before || 1),
      time: campaign.reminder_schedule.use_relative_schedule ? null : (time.time || '09:00'),
      days_before_relative: campaign.reminder_schedule.use_relative_schedule ? (time.days_before_relative || 0) : null,
      hours_before: campaign.reminder_schedule.use_relative_schedule ? (time.hours_before || 0) : null,
      minutes_before: campaign.reminder_schedule.use_relative_schedule ? (time.minutes_before || 0) : null,
      messages: []
    };

    // Clean up messages
    const cleanedMessages = time.messages.map(message => {
      const activeChannelConfig = message[`${campaign.channel}_config`] || {};
      
      const cleanedConfigs = {
        email_config: campaign.channel === 'email' ? {
          // Preserve the email config ID
          id: activeChannelConfig.id,
          content: message.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          from_name: activeChannelConfig.from_name || '',
          reply_to: activeChannelConfig.reply_to || '',
          priority: activeChannelConfig.priority || 'normal',
          track_opens: activeChannelConfig.track_opens ?? true,
          track_clicks: activeChannelConfig.track_clicks ?? true,
          attachments: activeChannelConfig.attachments || []
        } : null,
        sms_config: campaign.channel === 'sms' ? {
          // Preserve the SMS config ID
          id: activeChannelConfig.id,
          content: message.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          priority: activeChannelConfig.priority || 'normal',
          track_delivery: activeChannelConfig.track_delivery ?? true,
          track_replies: activeChannelConfig.track_replies ?? true,
          media_urls: activeChannelConfig.media_urls || []
        } : null,
        voice_config: campaign.channel === 'voice' ? {
          // Preserve the voice config ID
          id: activeChannelConfig.id,
          content: message.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          platform: activeChannelConfig.platform || 'bland_ai',
          voice_id: activeChannelConfig.voice_id || 'alexa',
          voice_name: activeChannelConfig.voice_name || 'Alexa (Female)',
          temperature: activeChannelConfig.temperature || 0.7,
          max_duration: activeChannelConfig.max_duration || 5,
          retry_attempts: activeChannelConfig.retry_attempts || 3,
          voicemail_message: activeChannelConfig.voicemail_message || '',
          voicemail_action: activeChannelConfig.voicemail_action || 'hangup',
          record_call: activeChannelConfig.record_call ?? false,
          call_timeout: activeChannelConfig.call_timeout || 60,
          // Use platform-specific configuration with ID preservation
          ...getPlatformSpecificConfigWithId(activeChannelConfig),
          webhook_config: {
            id: activeChannelConfig.webhook_config?.id,
            webhook: activeChannelConfig.webhook_config?.webhook || '',
            webhook_events: activeChannelConfig.webhook_config?.webhook_events || [],
            ...activeChannelConfig.webhook_config
          },
          metadata: activeChannelConfig.metadata || {}
        } : null,
        chat_config: campaign.channel === 'chat' ? {
          // Preserve the chat config ID
          id: activeChannelConfig.id,
          content: message.content || '',
          from_endpoint: activeChannelConfig.from_endpoint || '',
          platform: activeChannelConfig.platform || 'whatsapp',
          priority: activeChannelConfig.priority || 'normal',
          track_delivery: activeChannelConfig.track_delivery ?? true,
          track_read: activeChannelConfig.track_read ?? true,
          media_urls: activeChannelConfig.media_urls || [],
          quick_replies: activeChannelConfig.quick_replies || []
        } : null
      };

      return {
        // Preserve the message ID
        id: message.id,
        template: message.template || '',
        ...cleanedConfigs
      };
    });

    return {
      ...cleanedTime,
      messages: cleanedMessages
    };
  });

  // Clean up reminder schedule
  const cleanedReminderSchedule = {
    // Preserve the reminder schedule ID
    id: campaign.reminder_schedule.id,
    timezone: campaign.reminder_schedule.timezone || 'UTC',
    business_hours_only: campaign.reminder_schedule.business_hours_only ?? false,
    use_relative_schedule: campaign.reminder_schedule.use_relative_schedule ?? false,
    reminder_times: cleanedReminderTimes
  };

  // Return cleaned campaign
  return {
    // Preserve the campaign ID
    id: campaign.id,
    name: campaign.name || '',
    description: campaign.description || '',
    account: campaign.account || null,
    crm_campaign: campaign.crm_campaign || null,
    channel: campaign.channel || 'sms',
    campaign_type: 'reminder',
    start_date: campaign.start_date || null,
    end_date: campaign.end_date || null,
    is_ongoing: campaign.is_ongoing ?? false,
    active: campaign.active ?? true,
    status: campaign.status || 'draft',
    reminder_schedule: cleanedReminderSchedule,
    enable_opt_out: campaign.enable_opt_out ?? false,
    initial_opt_out_notice: campaign.initial_opt_out_notice || '',
    opt_out_message: campaign.opt_out_message || '',
    // Retry configuration
    retry_strategy_id: campaign.retry_strategy_id || null,
    max_retries: campaign.max_retries || null
  };
};

/**
 * Creates a default reminder campaign configuration
 * @param {string} channel - The communication channel
 * @returns {Object} The default campaign configuration
 */
export const createDefaultReminderCampaign = (channel = 'email') => ({
  name: '',
  description: '',
  account: '',
  crm_campaign: '',
  start_date: null,
  end_date: null,
  active: true,
  is_ongoing: false,
  campaign_type: 'reminder',
  channel,
  template: '',
  status: 'draft',
  auto_enroll_new_leads: false,
  auto_enroll_filters: [],
  lead_filters: [],
  // Retry configuration
  retry_strategy_id: null,
  max_retries: null,
  enable_opt_out: true,
  initial_opt_out_notice: "Reply STOP to opt out of further messages.",
  opt_out_message: "You've successfully opted out and will no longer receive messages from this number. Reply START at any time to resume.",
  reminder_schedule: {
    use_relative_schedule: false,
    business_hours_only: false,
    timezone: 'UTC',
    reminder_times: [{
      days_before: 1,
      time: '09:00',
      days_before_relative: null,
      hours_before: null,
      minutes_before: null,
      messages: [{
        template: '',
        email_config: {
          from_endpoint: '',
          from_name: '',
          reply_to: '',
          priority: 'normal',
          track_delivery: true,
          track_replies: true,
          track_opens: true,
          track_clicks: true,
          attachments: []
        },
        sms_config: {
          from_endpoint: '',
          priority: 'normal',
          track_delivery: true,
          track_replies: true,
          media_urls: []
        },
        voice_config: {
          from_endpoint: '',
          platform: 'bland_ai',
          voice_id: 'alexa',
          voice_name: 'Alexa (Female)',
          temperature: 0.7,
          max_duration: 5,
          retry_attempts: 3,
          voicemail_message: '',
          voicemail_action: 'hangup',
          record_call: false,
          call_timeout: 60,
          platform_config: {
            task: '',
            pathway_id: '',
            pathway_version: 1,
            first_sentence: '',
            background_track: '',
            model: 'base',
            wait_for_greeting: false,
            block_interruptions: false,
            interruption_threshold: 100,
            sensitive_voicemail_detection: false,
            transfer_phone_number: '',
            transfer_list: {},
            language: 'en-US',
            voicemail_sms: {},
            dispatch_hours: {},
            ignore_button_press: false,
            timezone: 'America/New_York',
            request_data: {},
            tools: [],
            start_time: '',
            voicemail_action: {},
            retry: { wait: 300, voicemail_action: 'hangup', voicemail_message: '' },
            max_duration: 5,
            record: false,
            webhook: '',
            webhook_events: [],
            metadata: {},
            citation_schema_id: '',
    
            available_tags: [],
            precall_dtmf_sequence: '',
            dynamic_data: [],
            keywords: [],
            pronunciation_guide: [],
            local_dialing: false
          },
          webhook_config: {
            webhook: '',
            webhook_events: []
          },
          metadata: {}
        },
        chat_config: {
          from_endpoint: '',
          platform: 'whatsapp',
          priority: 'normal',
          track_delivery: true,
          track_read: true,
          quick_replies: []
        }
      }]
    }]
  }
}); 